import React, { useState } from "react";
import InlineSVG from "react-inlinesvg";
import { cn } from "src/lib/cn";
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "src/sudokit/components/Dialog";
import { Input } from "src/sudokit/components/Input";
import { SudoButton } from "src/sudokit/components/SudoButton";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { Switch } from "src/sudokit/components/Switch";
import { Text } from "src/sudokit/components/Text";
import { Textarea } from "src/sudokit/components/Textarea";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { CreatePartnerCodeParams, PartnerCode, UpdatePartnerCodeParams } from "./PartnerCodesQueries";

interface PartnerCodeFormProps {
  code?: PartnerCode;
  onSubmit: (data: CreatePartnerCodeParams | UpdatePartnerCodeParams) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

interface FormData {
  code: string;
  type: "partner" | "event";
  credit_amount: number;
  is_active: boolean;
  notes: string;
  max_signup_count: number;
  landing_page: string;
  partner_name: string;
}

export const PartnerCodeForm: React.FC<PartnerCodeFormProps> = ({ code, onSubmit, onCancel, isSubmitting = false }) => {
  const isEditing = !!code;

  const [formData, setFormData] = useState<FormData>({
    code: code?.code || "",
    type: code?.type || "partner",
    credit_amount: code?.credit_amount || 200000,
    is_active: code?.is_active ?? true,
    notes: code?.notes || "",
    max_signup_count: code?.max_signup_count || 0,
    landing_page: code?.metadata?.landing_page || "",
    partner_name: code?.metadata?.partner_name || "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === "credit_amount" || name === "max_signup_count") {
      setFormData((prev) => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    if (name === "code" && value?.trim()) {
      if (!/^[a-zA-Z0-9-_]+$/.test(value)) {
        setErrors((prev) => ({ ...prev, code: "The code can only contain letters, numbers, hyphens, and underscores" }));
      }
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.code.trim()) {
      newErrors.code = "Code is required";
    } else if (!/^[a-zA-Z0-9-_]+$/.test(formData.code)) {
      newErrors.code = "Code can only contain letters, numbers, hyphens, and underscores";
    }

    if (formData.credit_amount < 0) {
      newErrors.credit_amount = "Credit amount must be positive";
    } else if (formData.credit_amount > 10000000) {
      newErrors.credit_amount = "Credit amount too large";
    }

    if (formData.max_signup_count < 0) {
      newErrors.max_signup_count = "Max signup count must be positive";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const { landing_page, partner_name, ...restFormData } = formData;
    const metadata = {
      ...code?.metadata,
      landing_page: landing_page || undefined,
      partner_name: partner_name || undefined,
    };

    if (isEditing) {
      onSubmit({
        id: code.id,
        ...restFormData,
        metadata,
      } as UpdatePartnerCodeParams);
    } else {
      onSubmit({
        ...restFormData,
        metadata,
      } as CreatePartnerCodeParams);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEditing ? "Edit Partner Code" : "Create Partner Code"}</DialogTitle>
        <DialogDescription>{isEditing ? "Update the details of this partner code." : "Create a new partner code to track referrals and provide bonus credits."}</DialogDescription>
      </DialogHeader>

      <form
        onSubmit={handleSubmit}
        className="mt-6 space-y-6"
      >
        <WithReset>
          <WithReset className="flex flex-row gap-2 items-center">
            <div className="w-full">
              <Text className="block text-sm font-medium text-app-grey-0 mb-2">Partner Code</Text>
              <div className="relative">
                <InlineSVG
                  src="/assets/chain-linked.svg"
                  className="absolute left-3 top-3 w-5 h-5 [&_path]:fill-app-grey-0 z-10"
                />
                <Input
                  mode="default"
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  disabled={isEditing}
                  className={cn(
                    "w-full pl-11 pr-3 py-2.5 rounded-xl text-app-grey-0",
                    "transition-all duration-200",
                    isEditing ? "opacity-50 cursor-not-allowed" : "",
                    errors.code ? "border-red-500 focus:ring-red-500" : "border-app-menu-color-hover",
                  )}
                  placeholder="my-partner-code"
                />
              </div>
            </div>

            <WithReset className="flex flex-col">
              <Text className="block text-sm font-medium text-app-grey-0 mb-2">Type</Text>
              <WithReset className="flex z-50">
                <MenuSelect
                  label="Type"
                  value={formData.type}
                  onChange={(value) => setFormData((prev) => ({ ...prev, type: value as "partner" | "event" }))}
                  options={[
                    {
                      value: "partner",
                      label: "Partner",
                    },
                    {
                      value: "event",
                      label: "Event",
                    },
                  ]}
                  placeholder="Select type"
                />
              </WithReset>
            </WithReset>
          </WithReset>

          {errors.code && <Text className="text-sm text-red-500 text-left mt-2 pl-2">{errors.code}</Text>}
          {!isEditing && formData.code && (
            <p className="mt-2 text-xs text-app-grey-1 flex items-center gap-1 justify-center">
              <InlineSVG
                src="/assets/world.svg"
                className="w-4 h-4 [&_path]:fill-app-grey-2"
              />
              sudowrite.com/{formData.type === "partner" ? "partners" : "events"}/{formData.code}
            </p>
          )}
        </WithReset>

        <div>
          <div className="mb-2">
            <Text className="text-sm font-medium text-app-grey-0">Bonus Credits</Text>
            <Text className="text-xs text-app-grey-1">Number of credits to grant when user subscribes (e.g., 200,000 for 200K credits)</Text>
          </div>
          <div className="relative">
            <InlineSVG
              src="/assets/pricing/credits.svg"
              className="absolute left-3 top-3 w-5 h-5 [&_path]:fill-app-grey-0 z-10"
            />
            <div className="flex flex-row gap-2 w-full items-center justify-between">
              <div className="w-full">
                <Input
                  mode="default"
                  type="number"
                  id="credit_amount"
                  name="credit_amount"
                  value={formData.credit_amount}
                  onChange={handleChange}
                  className={cn(
                    "w-full pl-11 pr-3 py-2.5 rounded-xl border bg-app-menu-color text-app-grey-0",
                    "transition-all duration-200",
                    errors.credit_amount ? "border-red-500 focus:ring-red-500" : "border-app-menu-color-hover",
                  )}
                  placeholder="200000"
                />
              </div>

              <div className="flex flex-row gap-2 items-center w-auto">
                <Text className="text-xl font-medium text-app-grey-2">=</Text>
                <Text className="text-xl font-medium text-app-grey-2">{Intl.NumberFormat("en-US").format(formData.credit_amount)}</Text>
              </div>
            </div>
          </div>
          {errors.credit_amount && (
            <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
              <InlineSVG
                src="/assets/warning.svg"
                className="w-4 h-4 [&_path]:fill-red-500"
              />
              {errors.credit_amount}
            </p>
          )}
        </div>

        <div>
          <div className="mb-2">
            <Text className="text-sm font-medium text-app-grey-0">Max Signup Count</Text>
            <Text className="text-xs text-app-grey-1">Maximum number of users who can sign up with this code. Set to 0 for unlimited signups.</Text>
          </div>
          <div className="relative">
            <InlineSVG
              src="/assets/character-icon.svg"
              className="absolute left-3 top-3 w-5 h-5 [&_path]:fill-app-grey-0 z-10"
            />
            <Input
              mode="default"
              type="number"
              id="max_signup_count"
              name="max_signup_count"
              value={formData.max_signup_count}
              onChange={handleChange}
              className={cn(
                "w-full pl-11 pr-3 py-2.5 rounded-xl border bg-app-menu-color text-app-grey-0",
                "transition-all duration-200",
                errors.max_signup_count ? "border-red-500 focus:ring-red-500" : "border-app-menu-color-hover",
              )}
              placeholder="0"
            />
          </div>
          {errors.max_signup_count && (
            <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
              <InlineSVG
                src="/assets/warning.svg"
                className="w-4 h-4 [&_path]:fill-red-500"
              />
              {errors.max_signup_count}
            </p>
          )}
        </div>

        <div>
          <div className="mb-2">
            <Text className="text-sm font-medium text-app-grey-0">Landing Page</Text>
            <Text className="text-xs text-app-grey-1">Custom landing page path to redirect users after clicking the partner link (optional, defaults to signup page). By default the base is editor.sudowrite.com, for sudowrite.com explicitly paste in the link.</Text>
          </div>
          <div className="relative">
            <InlineSVG
              src="/assets/world.svg"
              className="absolute left-3 top-3 w-5 h-5 [&_path]:fill-app-grey-0 z-10"
            />
            <Input
              mode="default"
              type="text"
              id="landing_page"
              name="landing_page"
              value={formData.landing_page}
              onChange={handleChange}
              className={cn(
                "w-full pl-11 pr-3 py-2.5 rounded-xl border bg-app-menu-color text-app-grey-0",
                "transition-all duration-200",
                errors.landing_page ? "border-red-500 focus:ring-red-500" : "border-app-menu-color-hover",
              )}
              placeholder="/signup-special, sudowrite.com/michael"
            />
          </div>
          {errors.landing_page && (
            <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
              <InlineSVG
                src="/assets/warning.svg"
                className="w-4 h-4 [&_path]:fill-red-500"
              />
              {errors.landing_page}
            </p>
          )}
        </div>

        <div>
          <div className="mb-2">
            <Text className="text-sm font-medium text-app-grey-0">Partner Name</Text>
            <Text className="text-xs text-app-grey-1">Display name for this partner (optional, used for tracking and display purposes)</Text>
          </div>
          <div className="relative">
            <InlineSVG
              src="/assets/character-icon.svg"
              className="absolute left-3 top-3 w-5 h-5 [&_path]:fill-app-grey-0 z-10"
            />
            <Input
              mode="default"
              type="text"
              id="partner_name"
              name="partner_name"
              value={formData.partner_name}
              onChange={handleChange}
              className={cn(
                "w-full pl-11 pr-3 py-2.5 rounded-xl border bg-app-menu-color text-app-grey-0",
                "transition-all duration-200",
                errors.partner_name ? "border-red-500 focus:ring-red-500" : "border-app-menu-color-hover",
              )}
              placeholder="The Big Lebowski Inc. (Optional)"
            />
          </div>
          {errors.partner_name && (
            <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
              <InlineSVG
                src="/assets/warning.svg"
                className="w-4 h-4 [&_path]:fill-red-500"
              />
              {errors.partner_name}
            </p>
          )}
        </div>

        <div>
          <Text className="text-sm font-medium text-app-grey-0 mb-2">Notes</Text>
          <Textarea
            mode="default"
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2.5 rounded-xl border border-app-menu-color-hover bg-app-menu-color text-app-grey-0 placeholder:text-app-grey-2 focus:outline-none transition-all duration-200 resize-none"
            placeholder="Notes about this partner or event..."
          />
        </div>

        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-3">
            <label
              htmlFor="is_active"
              className="text-sm font-medium text-app-grey-0"
            >
              Active Status
            </label>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_active: checked }))}
            />
          </div>
          <span className={cn("text-xs font-semibold px-3 py-1 rounded-lg", formData.is_active ? "bg-green-500/10 text-green-500" : "bg-app-grey-3/30 text-app-grey-1")}>
            {formData.is_active ? "Active" : "Inactive"}
          </span>
        </div>

        <DialogFooter>
          <SudoButton
            type="button"
            variant="secondaryGrey"
            onClick={onCancel}
          >
            Cancel
          </SudoButton>
          <SudoButton
            type="submit"
            variant="primary"
            state={isSubmitting ? "loading" : "default"}
            disabled={isSubmitting}
          >
            {isEditing ? "Update Code" : "Create Code"}
          </SudoButton>
        </DialogFooter>
      </form>
    </>
  );
};
